/**
 * useDocuments — the React binding for the Document Bank.
 *
 * Strict layering: the page calls THIS hook; the hook calls {@link TeamDocumentStore}
 * (and the existing Goal / Workstream / Task stores plus the sibling Service /
 * Decision stores) to resolve linked-entity titles CLIENT-SIDE for the cards,
 * pickers and relationship views; the stores call the api facade. The page never
 * imports a store or the api directly.
 *
 * The AI-STRUCTURE helper ({@link structureDocumentFromNotes}) is exposed here
 * bound to the real Bedrock converse op via the facade, so the register form can
 * offer "structure from notes → human verifies" without touching the api itself.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { KnowledgeBaseApi, type KbSource } from '../../api';
import { GoalStore } from '../goals/goalStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { TaskStore } from '../tasks/taskStore';
import { ServiceStore } from '../services/serviceStore';
import { DecisionStore } from '../decisions/decisionStore';
import { TeamDocumentStore } from './documentStore';
import { sortDocuments } from './documentModel';
import {
  structureDocumentFromNotes,
  type StructuredDocumentFields,
} from './aiStructure';
import type {
  DocumentStatus,
  TeamDocument,
  TeamDocumentDraft,
  TeamDocumentSummary,
} from './types';
import type { GoalSummary } from '../goals/types';
import type { WorkstreamSummary } from '../workstreams/types';
import type { Task } from '../tasks/types';
import type { ServiceSummary } from '../services/types';
import type { DecisionSummary } from '../decisions/types';

export interface UseDocumentsResult {
  documents: TeamDocumentSummary[];
  goals: GoalSummary[];
  workstreams: WorkstreamSummary[];
  tasks: Task[];
  services: ServiceSummary[];
  decisions: DecisionSummary[];
  currentAlias: string;
  /** id -> title maps for rendering links / relationships. */
  goalTitles: Map<string, string>;
  workstreamTitles: Map<string, string>;
  storyTitles: Map<string, string>;
  serviceTitles: Map<string, string>;
  decisionTitles: Map<string, string>;
  documentTitles: Map<string, string>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  get: (id: string) => Promise<TeamDocument | undefined>;
  create: (draft: TeamDocumentDraft) => Promise<TeamDocument>;
  update: (id: string, draft: TeamDocumentDraft) => Promise<TeamDocument | undefined>;
  setStatus: (id: string, status: DocumentStatus) => Promise<void>;
  remove: (id: string) => Promise<void>;
  /** AI-STRUCTURE: structure free-form notes into partial draft fields (Bedrock). */
  structureFromNotes: (notes: string) => Promise<StructuredDocumentFields>;
  /** Semantic (KB) discovery: retrieve relevant passages from the knowledge base. */
  kbSearch: (query: string, numberOfResults?: number) => Promise<KbSource[]>;
}

export function useDocuments(): UseDocumentsResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      documents: new TeamDocumentStore(api),
      goals: new GoalStore(api),
      workstreams: new WorkstreamStore(api),
      tasks: new TaskStore(api),
      services: new ServiceStore(api),
      decisions: new DecisionStore(api),
    }),
    [api],
  );

  const [documents, setDocuments] = useState<TeamDocumentSummary[]>([]);
  const [goals, setGoals] = useState<GoalSummary[]>([]);
  const [workstreams, setWorkstreams] = useState<WorkstreamSummary[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [decisions, setDecisions] = useState<DecisionSummary[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [docRows, goalRows, wsRows, allTasks, serviceRows, decisionRows, identity] =
        await Promise.all([
          stores.documents.listSummaries(),
          stores.goals.listSummaries(),
          stores.workstreams.listSummaries(),
          stores.tasks.listAll(),
          stores.services.listSummaries(),
          stores.decisions.listSummaries(),
          api.identityProvider.getIdentity(),
        ]);
      setDocuments(sortDocuments(docRows));
      setGoals(goalRows);
      setWorkstreams(wsRows);
      setTasks(allTasks);
      setServices(serviceRows);
      setDecisions(decisionRows);
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [stores, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const goalTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of goals) m.set(g.id, g.title);
    return m;
  }, [goals]);

  const workstreamTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workstreams) m.set(w.id, w.title);
    return m;
  }, [workstreams]);

  const storyTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tasks) m.set(t.id, t.title);
    return m;
  }, [tasks]);

  const serviceTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of services) m.set(s.id, s.name);
    return m;
  }, [services]);

  const decisionTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of decisions) m.set(d.id, d.decision);
    return m;
  }, [decisions]);

  const documentTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of documents) m.set(d.id, d.title);
    return m;
  }, [documents]);

  const get = useCallback((id: string) => stores.documents.get(id), [stores]);

  const create = useCallback(
    async (draft: TeamDocumentDraft) => {
      const saved = await stores.documents.create(draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const update = useCallback(
    async (id: string, draft: TeamDocumentDraft) => {
      const existing = await stores.documents.get(id);
      if (!existing) return undefined;
      const saved = await stores.documents.applyDraft(existing, draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const setStatus = useCallback(
    async (id: string, status: DocumentStatus) => {
      await stores.documents.setStatus(id, status);
      await refresh();
    },
    [stores, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await stores.documents.softDelete(id);
      await refresh();
    },
    [stores, refresh],
  );

  const structureFromNotes = useCallback(
    (notes: string) => structureDocumentFromNotes(api.inference, notes),
    [api],
  );

  const kbSearch = useCallback(
    async (query: string, numberOfResults = 8): Promise<KbSource[]> => {
      const response = await api.knowledgeBase.retrieve({
        query,
        numberOfResults,
      });
      return KnowledgeBaseApi.parseRetrieveResults(response.results);
    },
    [api],
  );

  return {
    documents,
    goals,
    workstreams,
    tasks,
    services,
    decisions,
    currentAlias,
    goalTitles,
    workstreamTitles,
    storyTitles,
    serviceTitles,
    decisionTitles,
    documentTitles,
    loading,
    error,
    refresh,
    get,
    create,
    update,
    setStatus,
    remove,
    structureFromNotes,
    kbSearch,
  };
}
